import { intersection, flatten, remove } from 'lodash';
import { getLiftTypesByCategory } from '../enums/lift-types';
import segmentInputsConfig from '../../scripts/segment-inputs-config/segment-config';
import { camelToDash, deleteProperty } from '../../scripts/utils';
import { isDealRent } from './rent-deal.service';
import { IS_AUTOPROLONG_DISABLED, USE_CHECK_SERVICE } from '../../../../shared/constants/config';
import { selectIsArchived } from '../../../../shared/selectors/offer';

const { addFormState } = window || {};

const offer = addFormState && addFormState.offer;
const offerId = offer && addFormState.offer.id;

// Состояние объявления: новое или редактируемое
export const isNewOffer = !offerId;
// Флаг фичи «Проверка собственника в Росреестре»
export const isRosreestrFeatureEnabled = addFormState && addFormState.rosReestrFeatureEnabled;

const events = {
  cleanModel: 'adState.cleanModel'
};

const isdr = isDealRent();

/**
 * Модели, которые надо отчистить перед отправкой, чтоб не получить ошибку валидации сервера
 * ключ - путь к модели относительно объекта ad, например building.year
 * значение - массив директив-полей, использующих эту модель
 * Если ни одно из полей модели не актуально для текущей категории - модель будет удалена
 */
const modelsToClean = {
  'minArea': ['cianAfAboutObjectCommercialAreaRange'],
  'building.materialType': [
    'cianAfAboutBuildingMaterialTypeAndSeries',
    'cianAfAboutBuildingCommercialMaterialType',
    'cianAfAboutObjectMaterialTypeHouse',
    'cianAfAboutBuildingNewBuildingMaterialType'
  ],
  'taxNumber': ['cianAfCommonTaxNumber']
};

export default [
  'apiService',
  'adSegmentsService',
  'categoryService',
  'homeownerService',
  '$rootScope',
  'placingTermsApi',
  'rentHelperService',
  'store',
  function (
    apiService,
    adSegmentsService,
    categoryService,
    homeownerService,
    $rootScope,
    placingTermsApi,
    rentHelperService,
    store,
  ) {
  const state = store.getState();

  const isArchived = selectIsArchived(state);

  const svc = {
    isNewOffer,
    // менялась ли категория после открытия
    categoryChanged: false,
    isArchived,
  };

  // отчищаем модели полей, неактуальных для данной категории
  const cleanModels = ad => {
    if (ad.category) {
      const { objectType, rentDuration } = categoryService.getInfoByCategory(ad.category);
      // При очистке модели нас интересуют все сегменты, в том числе и скрытые на короткой форме
      // см. баг https://cianru.atlassian.net/browse/CSSSR-7867

      const showRentHelper = rentHelperService.showRentHelperTrap();

      const segments = adSegmentsService.getSegments({
        objectType,
        rentDuration,
        ignoreShortForm: true,
        showRentHelperTrap: showRentHelper,
        showHomeownerWarning: homeownerService.getHomeOwnerWarningVisibility(ad),
      });

      const segmentFields = segments.map(s => segmentInputsConfig(ad.category, s)).filter(i => i).map(item => item.map(item => item.hasOwnProperty('name') ? item.name : item));
      const allFields = flatten(segmentFields); // директивы-поля актуальные для данной категории в ненормализованном виде (cian-af-...)
      Object.keys(modelsToClean).forEach(model => {
        const modelFields = modelsToClean[model].map(camelToDash); // поля, использующие данную модель
        if (intersection(modelFields, allFields).length == 0) { // отчищаем модель, если ни одно поле не актуально для данной категории
          deleteProperty(ad, model);
        }
      });
    }
  };

  /**
   * Зарегистрировать обработчик для отчистки модели перед отправкой,
   * @param handler - function(event, ad), где ad - объект отправляемый на сервер
   */
  svc.onCleanAdModel = handler => {
    return $rootScope.$on(events.cleanModel, handler);
  };

  /**
   * Зачищает входящий объект объявления для отправки на сервер и возвращает новый объект.
   *
   * @param {IOffer} объект объявления
   * @param {Boolean} функция была вызвана при сохранении Активного черновика
   *
   * @returns {IOffer}
   */
  svc.cleanAdModel = (adv, isActiveDraft = false) => {
    const ad = angular.copy(adv);

    svc.cleanAdModelWithMutate(ad, isActiveDraft);

    return ad;
  };

  /**
   * Зачищает входящий объект объявления для отправки на сервер.
   * Мутирует входящий объект объявления.
   *
   * @param {IOffer} объект объявления
   * @param {Boolean} функция была вызвана при сохранении Активного черновика
   *
   * @returns {Nothing}
   */
  svc.cleanAdModelWithMutate = (ad, isActiveDraft = false) => {
    // В новостройках не отсылаем видео
    if (ad.category === 'newBuildingFlatSale') delete ad.videos;

    if (ad.geo) {
      // Удалить метро если не заполено
      if (ad.geo.undergrounds && !isActiveDraft) {
        ad.geo.undergrounds = ad.geo.undergrounds.filter(u => u.id);
      }

      // Удалить шоссе если не заполено
      if (ad.geo.highways && !isActiveDraft) {
        ad.geo.highways = ad.geo.highways.filter(h => h.id && h.name && typeof(h.distance) !== "undefined" && h.distance !== null);
      }

      if (ad.geo.jk && ad.geo.jk.developer && !ad.geo.jk.developer.name && !ad.geo.jk.developer.id) {
        delete ad.geo.jk.developer;
      }

      if (ad.geo.jk && !ad.geo.jk.name && !ad.geo.jk.id && !ad.geo.jk.developer) {
        delete ad.geo.jk;
      }
    }

    if (ad.building) {
      if (String(ad.building.cargoLiftsCount) === '0') {
        ad.building.cargoLiftsCount = null;
      }

      if (String(ad.building.passengerLiftsCount) === '0') {
        ad.building.passengerLiftsCount = null;
      }
    }

    // Если сдается дом целиком, не нужно передавать значение части
    if (!ad.isRentByParts) {
      delete ad.rentByPartsDescription;
    }

    // Только для сдачи посуточно доступна часть дома
    if (ad.category !== 'dailyHouseRent') {
      delete ad.isRentByParts;
      delete ad.rentByPartsDescription;
    }

    // костыли, чтобы хрупкий бэкенд не ломался
    if (ad.bargainTerms) {
      // Питон не любит null в этих полях
      if (!isActiveDraft && !ad.bargainTerms.clientFee) ad.bargainTerms.clientFee = 0;
      if (!isActiveDraft && !ad.bargainTerms.agentFee) ad.bargainTerms.agentFee = 0;
      if (!isActiveDraft && !ad.bargainTerms.deposit) ad.bargainTerms.deposit = 0;

      if (ad.bargainTerms.agentBonus && (isActiveDraft
        ? !ad.bargainTerms.agentBonus.paymentType
        : !ad.bargainTerms.agentBonus.paymentType || ad.bargainTerms.agentBonus.value === null)
      ) {
        ad.bargainTerms.agentBonus = null;
      }
    }

    // удаляем объект самозанятого
    if (ad.selfEmployedData) {
      if (!ad.selfEmployedData.isEnabled) {
        ad.selfEmployedData.inn = null;
      }

      delete ad.selfEmployedData.isEnabled;
    }

    // удаляем лишние типы лифтов
    if (ad.building && ad.building.liftTypes) {
      const validLiftTypes = getLiftTypesByCategory(ad.category);
      if (validLiftTypes) {
        const validValues = validLiftTypes.map(i => i.value);
        remove(ad.building.liftTypes, i => !validValues.contains(i.type));
      }
      else {
        ad.building.liftTypes = undefined;
      }
    }

    // Удалить телефоны null - падает Backend
    if (ad.phones && Array.isArray(ad.phones)) {
      ad.phones = ad.phones.filter((item) => angular.isObject(item) && item.number);
    }

    if (placingTermsApi.isCalltrackingSelected() && Array.isArray(ad.phones) && ad.phones.length >= 1) {
      const calltracking = placingTermsApi.calltrackingOption;

      // Принудительный КТ имеет данные о том, сколько номеров можно подменить
      // Поэтому мы можем просто отбросить лишние номера
      if (calltracking.isCompulsory) {
        ad.phones = ad.phones.slice(0, calltracking.calltrackingPhoneSubstitutionsCount);

        return;
      }

      // Удаляем все номера, кроме выбранного в добровольном КТ
      if (ad.phones.length >= 2) {
        ad.phones = [ad.phones[ad.calltrackingPhoneIndex]];
      }
    }

    if (typeof ad.homeOwner === 'object' && ad.homeOwner && !ad.homeOwner.secondName) {
      // Удостоверяемся, что в информации о собственнике есть поле Отчество с пустой строкой, и заменяем её на null
      ad.homeOwner.secondName = null;
    }

    // Для посуточной оплаты всегда ставим автопролонгацию, за исключением аукциона
    if (
      ad.publishTerms &&
      !placingTermsApi.isAutoprolongAvailable(IS_AUTOPROLONG_DISABLED, USE_CHECK_SERVICE) &&
      Array.isArray(ad.publishTerms.terms) &&
      ad.publishTerms.terms.some(({ type, services }) => type === 'dailyTermless' && services.indexOf('auction') === -1)
    ) {
      ad.publishTerms.autoprolong = true;
    }

    cleanModels(ad);

    $rootScope.$emit(events.cleanModel, ad);
  };

  /*
  Переставляем телефоны местами, если заполнен второй и не заполнен первый
  */
  svc.handlePhones = (phones, form) => {
    const phonesFocus = angular.isDefined(form) && ((form['phones.0'] && form['phones.0'].$focus) || (form['phones.1'] && form['phones.1'].$focus));
    // Если телефоны под фокусом возвращаем исходный массив
    // Если нет фокуса и заполнено второе поле, а первое пустое меняем местами
    return !phonesFocus && phones && Array.isArray(phones) && phones[0] === null && phones[1] !== null ? phones.sort() : phones;
  };

  svc.publish = (ad, checkArchivedState) => {
    // Проставить флаг published или что-то такое прямо в объявление, от этого меняется немного поведение второстепенных блоков
    // отправить запрос postOffer, поменять урл если надо
    // Надо в урл записывать id после успеха от запроса

    // Здесь происходит мутация входного параметра. Это плохо.
    // Нужно добавить в параметры успешного выполнения промиса.
    svc.cleanAdModelWithMutate(ad);

    const params = {};
    let queryParams = '';

    if (checkArchivedState && svc.isArchived) {
      params.isArchived = 'true';
    }

    if (window.addFormState.isFromXML) {
      params.isFromXML = 1;
    }

    if (isdr) {
      params.isDealRent = 1;
    }

    if (Object.keys(params).length) {
      queryParams = '?' + Object.keys(params).map(key => key + '=' + params[key]).join('&');
    }

    return apiService.postOffer(ad, queryParams);
  };

  svc.checkStatus = ad => {
    return apiService.offerStatus(ad.objectGuid);
  };

  svc.setCategoryChanged = (value) => {
    this.categoryChanged = value;
  };

  return svc;
}];
